import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { Portal } from '@newfront-insurance/core-ui';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import { useQuery, useRouter } from '@newfront-insurance/next-router-provider';
import { useProvider, type Provider as ProvisionProvider } from '@newfront-insurance/react-provision';

import { MSGraphRoot, NovaChatContainer, NovaProvider, useNovaWrapperState, type NovaProviderCallbacks } from '../..';
import { FloatingButton } from '../../components/floating-button';
import { resolvePageAccountUuid } from '../../lib/account-bootstrap';

interface ChatOverlayProviderV2Props {
  children: React.ReactNode;
  authProvider: ProvisionProvider<AuthProviderContext>;
  transparentFloatingButton?: boolean;
  callbacks?: NovaProviderCallbacks;
  initialAccountUuid?: string;
}

/**
 * Simplified chat overlay provider using chat-v2 components.
 * All state management is handled internally by NovaProvider.
 * This wrapper only handles feature flags, portal rendering, and floating button.
 */
export function ChatOverlayProviderV2({
  children,
  authProvider,
  transparentFloatingButton,
  callbacks,
  initialAccountUuid,
}: ChatOverlayProviderV2Props) {
  const { router } = useRouter();
  const isChatOverlayEnabled = useFeatureFlag('nova-chat-overlay', false);
  const msGraphEnabled = useFeatureFlag('nova-tools-graph-api', false);
  const isDebugRoute = router.pathname.startsWith('/chat/debug');
  const isStandaloneMode = router.pathname.startsWith('/chat') && !isDebugRoute;
  const { userDetails } = useProvider(authProvider);
  const { uuid: userUuid, scopes } = userDetails ?? {};
  const queryAccountUuid = useQuery<string | undefined>('accountUuid');
  const pageAccountUuid = resolvePageAccountUuid({ initialAccountUuid, queryAccountUuid });

  return (
    <>
      {children}
      {isChatOverlayEnabled && userUuid && !isDebugRoute && (
        <Portal name="chat-overlay-portal" portalZIndex={1000}>
          <MSGraphRoot enabled={msGraphEnabled}>
            <NovaProvider
              userUuid={userUuid}
              scopes={scopes}
              authProvider={authProvider}
              isStandaloneMode={isStandaloneMode}
              initialAccountUuid={pageAccountUuid}
              callbacks={callbacks}
            >
              <FloatingButtonWrapper transparentFloatingButton={transparentFloatingButton} />
              <NovaChatContainer />
            </NovaProvider>
          </MSGraphRoot>
        </Portal>
      )}
    </>
  );
}

/**
 * Wrapper component to render FloatingButton using Nova state.
 * Must be rendered inside NovaProvider.
 */
function FloatingButtonWrapper({ transparentFloatingButton }: { transparentFloatingButton?: boolean }) {
  const { isChatVisible, isFloatingButtonVisible, hasNotifications, handleToggle } = useNovaWrapperState();
  const { router } = useRouter();
  const isStandaloneMode = router.pathname.startsWith('/chat') && !router.pathname.startsWith('/chat/debug');

  if (!isFloatingButtonVisible || isStandaloneMode || isChatVisible) {
    return null;
  }

  return (
    <FloatingButton
      onClick={handleToggle}
      hasNotifications={hasNotifications}
      isTransparent={transparentFloatingButton}
    />
  );
}
